// One list on a place's Transfers tab (owner, 2026-09-15): its transfers plus the location-history events no transfer
// explains. An asset entering a transfer's destination is that transfer's "arrived", and one leaving its origin is
// its "dispatched", so those two events are dropped. What stays: first placements, exits with no transfer (the asset
// then shows Missing / Off site), coming back home, missing / found.

export interface TransferEnds {
  from_site_id: string | null;
  to_site_id: string | null;
  transfer_lines: { asset_id: string }[];
}

export interface PlaceEvent {
  event: string;
  site_id: string | null;
  asset_id: string;
}

/** Keeps the events no transfer in `transfers` explains (see the file comment). */
export function unexplainedEvents<E extends PlaceEvent>(
  events: E[],
  transfers: TransferEnds[],
): E[] {
  const explained = new Set<string>();
  for (const t of transfers) {
    for (const l of t.transfer_lines) {
      if (t.to_site_id) explained.add(`entered|${l.asset_id}|${t.to_site_id}`);
      if (t.from_site_id) explained.add(`exited|${l.asset_id}|${t.from_site_id}`);
    }
  }
  return events.filter((e) => !explained.has(`${e.event}|${e.asset_id}|${e.site_id}`));
}

export type TransferStep = 'dispatched' | 'arrived' | 'received';

export interface TransferDates {
  status: string;
  created_at: string;
  updated_at: string;
  dispatched_at: string | null;
  awaiting_since: string | null;
  received_at: string | null;
}

/**
 * The one date a transfer row shows: its latest step (received, else arrived, else dispatched). A cancelled or
 * still-draft transfer has no step: when it was cancelled, or created.
 */
export function latestStep(m: TransferDates): { step: TransferStep | null; at: string } {
  if (m.status === 'cancelled') return { step: null, at: m.updated_at };
  if (m.received_at) return { step: 'received', at: m.received_at };
  if (m.awaiting_since) return { step: 'arrived', at: m.awaiting_since };
  if (m.dispatched_at) return { step: 'dispatched', at: m.dispatched_at };
  return { step: null, at: m.created_at };
}
